import { useCallback, RefObject, useEffect } from 'react'
import throttle from 'lodash/throttle'
import { useObserver } from 'mobx-react'
import { useRefDep } from './ref-dep'

export interface ScrollProvider {
  active: boolean
  more: () => unknown
  isDone: boolean
}

/**
 * Hook for providing infinite scroll monitoring.
 *
 * @param monitoringTargetRef
 * @param scrollProvider
 */
export function useInfiniteScroll(
  monitoringTargetRef: RefObject<HTMLElement>,
  scrollProvider: ScrollProvider
) {
  /**
   * Checks if the target is visible. If it is, calls the `more` function.
   */
  const checkBounds = useCallback(
    throttle(() => {
      const node = monitoringTargetRef.current

      if (!node || !scrollProvider.active || !scrollProvider.more) {
        return
      }

      const viewportHeight = getViewportHeight()
      const rect = node.getBoundingClientRect()
      const elemTop = rect.top
      const elemBottom = rect.bottom

      // Partially visible elements return true:
      const visible = elemTop < viewportHeight && elemBottom >= 0
      if (visible) {
        scrollProvider.more()
      }
    }, 100),
    [scrollProvider, useRefDep(monitoringTargetRef)]
  )

  /**
   * Subscribe to scroll events and an interval.
   */
  useEffect(() => {
    window.addEventListener('scroll', checkBounds)
    const interval = window.setInterval(checkBounds)
    return () => {
      window.removeEventListener('scroll', checkBounds)
      window.clearInterval(interval)
    }
  }, [checkBounds])

  /**
   * Returns an object that describes the provider's state.
   */
  return useObserver(() => ({
    active: scrollProvider.active,
    isDone: scrollProvider.isDone,
  }))
}

/**
 * Gets the view port height
 */
function getViewportHeight() {
  return window.innerHeight || document.documentElement.clientHeight
}
